import React, { createContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

// Define user type
export interface User {
  _id: string;
  username: string;
  email: string;
  firstName: string;
  lastName: string;
  role: 'admin' | 'manager' | 'staff';
  userType: 'admin' | 'homeowner' | 'outside_member' | 'other';
  isActive: boolean;
  settings: any;
  createdAt: string;
  updatedAt: string;
  lastLogin: string | null;
}

// Define auth context type
interface AuthContextType {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  login: (username: string, password: string) => Promise<void>;
  logout: () => void;
  register: (userData: any) => Promise<void>;
  clearError: () => void;
  checkAuth: () => Promise<boolean>;
}

// Create the auth context with default values
export const AuthContext = createContext<AuthContextType>({
  user: null,
  token: null,
  isAuthenticated: false,
  isLoading: true,
  error: null,
  login: async () => {},
  logout: () => {},
  register: async () => {},
  clearError: () => {},
  checkAuth: async () => false
});

// Define props for the provider
interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(localStorage.getItem('token'));
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const navigate = useNavigate();

  // Clear any error messages
  const clearError = () => {
    setError(null);
  };

  // Load user data if token exists
  const loadUser = useCallback(async () => {
    console.log('[AuthContext] loadUser: Starting loadUser function');
    
    // Check localStorage first
    const localStorageToken = localStorage.getItem('token');
    console.log('[AuthContext] loadUser: Token in localStorage:', !!localStorageToken);
    
    // Then check state
    console.log('[AuthContext] loadUser: Current token state:', token);
    
    // If token exists in localStorage but not in state, update state
    if (localStorageToken && !token) {
      console.log('[AuthContext] loadUser: Token exists in localStorage but not in state, updating state');
      setToken(localStorageToken);
      // Return early and let the next effect run handle the API call
      return;
    }
    
    if (!token) {
      console.log('[AuthContext] loadUser: No token in state or localStorage, returning');
      setIsLoading(false);
      return;
    }
    
    // Use AbortController to handle potential duplicate requests
    const controller = new AbortController();
    
    try {
      console.log('[AuthContext] loadUser: Attempting to verify token via api.verifyToken()');
      const res = await api.verifyToken(controller.signal);
      if (!controller.signal.aborted) {
        console.log('[AuthContext] loadUser: Token verified, user data:', res.user);
        setUser(res.user);
        setIsAuthenticated(true);
      }
    } catch (err: any) {
      if (!controller.signal.aborted && err.name !== 'AbortError') {
        console.error('[AuthContext] loadUser error:', err);
        localStorage.removeItem('token');
        console.log('[AuthContext] loadUser: Token removed from localStorage due to error');
        setToken(null);
        setUser(null);
        setIsAuthenticated(false);
        setError('Session expired. Please login again.');
      }
    } finally {
      if (!controller.signal.aborted) {
        setIsLoading(false);
      }
    }
    
    return () => {
      controller.abort();
    };
  }, [token]);

  // Check if user is authenticated
  const checkAuth = async (): Promise<boolean> => {
    if (!token) return false;
    try {
      await api.verifyToken();
      return true;
    } catch (err) {
      return false;
    }
  };

  // Login user
  const login = async (username: string, password: string) => {
    console.log('[AuthContext] login: Starting login process with username:', username);
    console.log('[AuthContext] login: Password length:', password.length);
    
    try {
      setIsLoading(true);
      clearError();
      
      console.log('[AuthContext] login: Calling api.login');
      const response = await api.login({ username, password });
      console.log('[AuthContext] login: API login response received:', JSON.stringify({
        message: response.message,
        hasToken: !!response.token,
        hasUser: !!response.user
      }));

      if (response && response.token) {
        console.log('[AuthContext] login: Token received, length:', response.token.length);
        console.log('[AuthContext] login: Token first 15 chars:', response.token.substring(0, 15));
        
        // DIRECT TOKEN TEST: Try calling API with token directly
        try {
          const testResult = await fetch('http://localhost:3001/api/auth/me', {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${response.token}`
            }
          });
          console.log('[AuthContext] DIRECT TOKEN TEST - Response status:', testResult.status);
          if (testResult.ok) {
            console.log('[AuthContext] DIRECT TOKEN TEST - Success! Token works directly.');
            const data = await testResult.json();
            console.log('[AuthContext] DIRECT TOKEN TEST - User data:', data);
          } else {
            console.error('[AuthContext] DIRECT TOKEN TEST - Failed. Token not accepted.');
          }
        } catch (testErr) {
          console.error('[AuthContext] DIRECT TOKEN TEST - Error:', testErr);
        }
        
        // Store token in localStorage
        localStorage.setItem('token', response.token);
        console.log('[AuthContext] login: Token stored in localStorage');
        
        // Immediately check if localStorage has the token
        const storedToken = localStorage.getItem('token');
        console.log('[AuthContext] login: Verified token in localStorage:', storedToken ? 'Yes' : 'No');
        if (storedToken) {
          console.log('[AuthContext] login: Stored token first 15 chars:', storedToken.substring(0, 15));
        }
        
        setToken(response.token);
        console.log('[AuthContext] login: Token state updated');
        setUser(response.user);
        setIsAuthenticated(true);
        
        // Redirect based on role
        console.log('[AuthContext] login: Redirecting to dashboard');
        navigate('/dashboard');
      } else {
        console.error('[AuthContext] login: Token not found in response:', response);
        setError('Login failed: No token received from server');
        setIsAuthenticated(false);
      }
    } catch (err: any) {
      const message = err.response?.data?.message || 'Login failed. Please try again.';
      console.error('[AuthContext] login error:', err);
      setError(message);
      setIsAuthenticated(false);
    } finally {
      setIsLoading(false);
    }
  };

  // Register user
  const register = async (userData: any) => {
    try {
      setIsLoading(true);
      clearError();

      const response = await api.register(userData);

      // Store token in localStorage and state
      localStorage.setItem('token', response.token);
      setToken(response.token);
      setUser(response.user);
      setIsAuthenticated(true);

      // Redirect to dashboard
      navigate('/dashboard');
    } catch (err: any) {
      const message = err.response?.data?.message || 'Registration failed. Please try again.';
      setError(message);
    } finally {
      setIsLoading(false);
    }
  };

  // Logout user
  const logout = () => {
    console.log('[AuthContext] logout: Logging out user.');
    // Remove token from localStorage
    localStorage.removeItem('token');
    console.log('[AuthContext] logout: Token removed from localStorage.');
    setToken(null);
    setUser(null);
    setIsAuthenticated(false);

    // Redirect to login page
    navigate('/login');
  };

  // Load user on initial render if token exists
  useEffect(() => {
    console.log('[AuthContext] Initial effect: calling loadUser');
    console.log('[AuthContext] Initial localStorage token:', !!localStorage.getItem('token'));
    console.log('[AuthContext] Initial token state:', !!token);
    console.log('[AuthContext] Initial isAuthenticated state:', isAuthenticated);
    loadUser();
    // Only depend on loadUser to prevent infinite loops
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadUser]);

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        isAuthenticated,
        isLoading,
        error,
        login,
        logout,
        register,
        clearError,
        checkAuth
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use the auth context
export const useAuth = () => React.useContext(AuthContext);
